/**
 * Handles the `?request=<insight id>` deep link that data-request notifications open
 * (`/agent?voice=1&request=<id>`): starts a conversation whose first message is the request's
 * question, hands it to the page once, and removes `request` from the URL so a reload does not
 * start another. A `?checkin=1` link (sent when check-ins are stale but no specific question is
 * pending) opens a fresh coach conversation instead. Other parameters, such as `voice`, are left
 * for their own handlers.
 *
 * @module apps/web/src/components/flux-agent/learning/useDataRequestDeepLink
 */

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fluxAgentApi, type FluxAgentConversation } from '@/lib/fluxAgentApi';
import { fluxAgentLearningApi } from '@/lib/fluxAgentLearningApi';

/** Start the conversation for a `request` or `checkin` deep link and pass it to `onStarted` exactly once. */
export function useDataRequestDeepLink(
  onStarted: (conversation: FluxAgentConversation) => void
): void {
  const [params, setParams] = useSearchParams();
  const requestId = params.get('request');
  const checkIn = !requestId && params.get('checkin') === '1';
  const handled = useRef<string | null>(null);
  const callback = useRef(onStarted);
  callback.current = onStarted;

  useEffect(() => {
    if (!requestId || handled.current === requestId) return;
    handled.current = requestId;
    void (async () => {
      const result = await fluxAgentLearningApi.startDataRequest(requestId);
      setParams(
        current => {
          const next = new URLSearchParams(current);
          next.delete('request');
          return next;
        },
        { replace: true }
      );
      if (result.ok) callback.current(result.data.conversation);
    })();
  }, [requestId, setParams]);

  useEffect(() => {
    if (!checkIn || handled.current === 'checkin') return;
    handled.current = 'checkin';
    void (async () => {
      const result = await fluxAgentApi.createConversation('coach');
      setParams(
        current => {
          const next = new URLSearchParams(current);
          next.delete('checkin');
          return next;
        },
        { replace: true }
      );
      if (result.ok) callback.current(result.data.conversation);
    })();
  }, [checkIn, setParams]);
}
